import { FC, useMemo } from "react";
import { Box, Paper, Typography } from "@mui/material";
import {
	EventActivityEvent,
	EventActivityRow,
	EventActivityStatus,
} from "../../../../../services/reports.service";
import StatusCell from "./StatusCell";

interface EventActivityPivotTableProps {
	events: EventActivityEvent[];
	rows: EventActivityRow[];
	totalDays?: number;
}

const ENROLLMENT_COL_WIDTH = 148;
const ORG_UNIT_COL_WIDTH = 140;
const COMPLETION_COL_WIDTH = 110;
const EVENT_SUBCOL_WIDTH = 48;
const FROZEN_BG = "#FFFFFF";
const HEADER_BG = "#F5F7FA";

const baseCell = {
	padding: "8px 12px",
	borderBottom: "1px solid #E0E0E0",
	whiteSpace: "nowrap" as const,
	fontSize: 13,
};

const EventActivityPivotTable: FC<EventActivityPivotTableProps> = ({
	events,
	rows,
	totalDays = 14,
}) => {
	const dayHeaders = useMemo(
		() => Array.from({ length: totalDays }, (_, i) => i + 1),
		[totalDays],
	);

	// Build a quick lookup so we can ask: row[dayIdx][eventId] → status (or undefined).
	const statusByRow = useMemo(
		() =>
			rows.map((row) => {
				const map = new Map<string, EventActivityStatus>();
				for (const day of row.days) {
					for (const sub of day.submissions) {
						map.set(`${day.day}:${sub.eventId}`, sub.status);
					}
				}
				return map;
			}),
		[rows],
	);

	if (events.length === 0) {
		return (
			<Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
				<Typography variant="body1" color="text.secondary">
					No events scheduled in this program&apos;s treatment plan.
				</Typography>
			</Paper>
		);
	}

	return (
		<Paper variant="outlined" sx={{ overflow: "hidden" }}>
			<Box sx={{ overflowX: "auto" }}>
				<Box
					component="table"
					sx={{
						borderCollapse: "separate",
						borderSpacing: 0,
						width: "max-content",
						minWidth: "100%",
					}}
				>
					<thead>
						<tr>
							<Box
								component="th"
								rowSpan={2}
								sx={{
									...baseCell,
									position: "sticky",
									left: 0,
									zIndex: 3,
									background: HEADER_BG,
									width: ENROLLMENT_COL_WIDTH,
									minWidth: ENROLLMENT_COL_WIDTH,
									textAlign: "left",
									fontWeight: 600,
								}}
							>
								Enrollment
							</Box>
							<Box
								component="th"
								rowSpan={2}
								sx={{
									...baseCell,
									position: "sticky",
									left: ENROLLMENT_COL_WIDTH,
									zIndex: 3,
									background: HEADER_BG,
									width: ORG_UNIT_COL_WIDTH,
									minWidth: ORG_UNIT_COL_WIDTH,
									textAlign: "left",
									fontWeight: 600,
									borderRight: "2px solid #BDBDBD",
								}}
							>
								Org unit
							</Box>
							{dayHeaders.map((d) => (
								<Box
									key={`day-${d}`}
									component="th"
									colSpan={events.length}
									sx={{
										...baseCell,
										background: HEADER_BG,
										textAlign: "center",
										fontWeight: 600,
										borderRight: "1px solid #BDBDBD",
									}}
								>
									Day {d}
								</Box>
							))}
							<Box
								component="th"
								rowSpan={2}
								sx={{
									...baseCell,
									position: "sticky",
									right: 0,
									zIndex: 3,
									background: HEADER_BG,
									width: COMPLETION_COL_WIDTH,
									minWidth: COMPLETION_COL_WIDTH,
									textAlign: "right",
									fontWeight: 600,
									borderLeft: "2px solid #BDBDBD",
								}}
							>
								Completion %
							</Box>
						</tr>
						<tr>
							{dayHeaders.map((d) =>
								events.map((ev, evIdx) => (
									<Box
										key={`day-${d}-event-${ev.eventId}`}
										component="th"
										sx={{
											...baseCell,
											background: HEADER_BG,
											textAlign: "center",
											fontWeight: 500,
											fontSize: 11,
											color: "text.secondary",
											width: EVENT_SUBCOL_WIDTH,
											minWidth: EVENT_SUBCOL_WIDTH,
											borderRight:
												evIdx === events.length - 1
													? "1px solid #BDBDBD"
													: "1px solid #EEEEEE",
										}}
										title={ev.name}
									>
										{ev.name}
									</Box>
								)),
							)}
						</tr>
					</thead>
					<tbody>
						{rows.map((row, rowIdx) => (
							<tr key={row.enrollmentId}>
								<Box
									component="td"
									sx={{
										...baseCell,
										position: "sticky",
										left: 0,
										zIndex: 2,
										background: FROZEN_BG,
										width: ENROLLMENT_COL_WIDTH,
										minWidth: ENROLLMENT_COL_WIDTH,
									}}
								>
									{/* SIMS-8895 (5): already resolved by the tab against the
									    account's displayBeneficiaryName — this component stays
									    presentational and takes no settings dependency. */}
									{row.participantName}
								</Box>
								<Box
									component="td"
									sx={{
										...baseCell,
										position: "sticky",
										left: ENROLLMENT_COL_WIDTH,
										zIndex: 2,
										background: FROZEN_BG,
										width: ORG_UNIT_COL_WIDTH,
										minWidth: ORG_UNIT_COL_WIDTH,
										borderRight: "2px solid #BDBDBD",
									}}
								>
									{row.orgUnit || "—"}
								</Box>
								{dayHeaders.map((d) =>
									events.map((ev, evIdx) => {
										const status = statusByRow[rowIdx].get(
											`${d}:${ev.eventId}`,
										);
										return (
											<Box
												key={`row-${row.enrollmentId}-d${d}-${ev.eventId}`}
												component="td"
												sx={{
													...baseCell,
													textAlign: "center",
													borderRight:
														evIdx === events.length - 1
															? "1px solid #BDBDBD"
															: "1px solid #EEEEEE",
												}}
											>
												{status ? <StatusCell status={status} /> : null}
											</Box>
										);
									}),
								)}
								<Box
									component="td"
									sx={{
										...baseCell,
										position: "sticky",
										right: 0,
										zIndex: 2,
										background: FROZEN_BG,
										textAlign: "right",
										fontWeight: 600,
										borderLeft: "2px solid #BDBDBD",
									}}
								>
									{row.completionPct.toFixed(1)}%
								</Box>
							</tr>
						))}
					</tbody>
				</Box>
			</Box>
		</Paper>
	);
};

export default EventActivityPivotTable;
